import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { UserManager, User, UserManagerSettings } from 'oidc-client-ts'
import { getOidcSettings } from '@/config/oidcSettings'
import { useEnvStore } from './envStore'
import { OpenAPI } from '@itemis-solutions/platform-client-sdk'
import { Config as PlatformWebComponentsConfig } from '@itemis-solutions/platform-web-components'

export const useAuthStore = defineStore('authStore', () => {
  // Dependencies
  const envStore = useEnvStore()

  // State
  const accessToken = ref<string | undefined>()
  const refreshToken = ref<string | undefined>()
  const expiresAt = ref<number | undefined>()
  const idToken = ref<string | undefined>()

  // UserManager instance (lazy-initialized)
  let userManager: UserManager | null = null

  // Computed
  const isAuthenticated = computed(() => !!accessToken.value)
  const isTokenExpired = computed(() => {
    if (!expiresAt.value) return true
    return new Date().getTime() >= expiresAt.value
  })

  // Private: Initialize UserManager
  function initUserManager(): UserManager {
    if (!userManager) {
      const settings = getOidcSettings(envStore.variables) as UserManagerSettings
      userManager = new UserManager(settings)

      // Subscribe to user loaded event
      userManager.events.addUserLoaded((user: User) => {
        setAuth(user)
      })

      // Subscribe to silent renew error
      userManager.events.addSilentRenewError((error) => {
        console.error('Silent renew error:', error)
      })
    }
    return userManager
  }

  // Actions
  function setAuth(user: User | null) {
    if (user) {
      accessToken.value = user.access_token
      refreshToken.value = user.refresh_token
      expiresAt.value = user.expires_at ? user.expires_at * 1000 : undefined
      idToken.value = user.id_token

      // Sync tokens to external libraries
      if (user.access_token) {
        OpenAPI.TOKEN = user.access_token
        PlatformWebComponentsConfig.ACCESS_TOKEN = user.access_token
      }
    } else {
      $reset()
    }
  }

  async function handleCallback(): Promise<string> {
    const manager = initUserManager()
    const user = await manager.signinCallback()
    setAuth(user ?? null)
    return (user?.state as string) || '/'
  }

  async function refreshAccessToken(): Promise<void> {
    const manager = initUserManager()
    const user = await manager.signinSilent()
    setAuth(user ?? null)
  }

  async function signIn(): Promise<void> {
    const manager = initUserManager()
    await manager.signinRedirect()
  }

  async function signOut(): Promise<void> {
    const manager = initUserManager()
    await manager.signoutRedirect()
    $reset()
  }

  function getUserManager(): UserManager {
    return initUserManager()
  }

  function $reset() {
    accessToken.value = undefined
    refreshToken.value = undefined
    expiresAt.value = undefined
    idToken.value = undefined
    PlatformWebComponentsConfig.ACCESS_TOKEN = ''
  }

  return {
    // State
    accessToken,
    refreshToken,
    expiresAt,
    idToken,
    // Computed
    isAuthenticated,
    isTokenExpired,
    // Actions
    setAuth,
    handleCallback,
    refreshAccessToken,
    signIn,
    signOut,
    getUserManager,
    $reset,
  }
})
